import { useEffect, useState } from "react";
import { useRouter } from "next/router";

/**
 * Opt-in viewport readout, for diagnosing "it looks too big on my machine"
 * reports. Append ?viewport=1 to any URL to show it; it renders nothing
 * otherwise, so it has no effect on the live site.
 *
 * Why this exists: a report of "everything is oversized" is almost always a
 * scaled display. A 1920px monitor at 150% Windows scaling gives a 1280px CSS
 * viewport, and only ~600px of CSS height once browser chrome is subtracted -
 * so the page is being asked to fit far less room than the raw resolution
 * suggests. `dpr` x `css` should equal roughly the screen size; when it does
 * not, OS scaling or browser zoom is in play.
 */
export default function ViewportDebug() {
  const router = useRouter();
  const [info, setInfo] = useState(null);

  const enabled =
    router.query?.viewport === "1" || router.query?.viewport === "true";

  useEffect(() => {
    if (!enabled) return;

    const read = () =>
      setInfo({
        css: `${window.innerWidth} x ${window.innerHeight}`,
        dpr: window.devicePixelRatio,
        screen: `${window.screen.width} x ${window.screen.height}`,
        device: `${Math.round(
          window.innerWidth * window.devicePixelRatio
        )} x ${Math.round(window.innerHeight * window.devicePixelRatio)}`,
        rootFontSize: getComputedStyle(document.documentElement).fontSize,
        headerH: getComputedStyle(document.documentElement)
          .getPropertyValue("--header-h")
          .trim(),
      });

    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, [enabled]);

  if (!enabled || !info) return null;

  const rows = [
    ["CSS viewport", info.css],
    ["Device pixel ratio", info.dpr],
    ["Screen", info.screen],
    ["Viewport in device px", info.device],
    ["Root font-size", info.rootFontSize],
    ["--header-h", info.headerH],
  ];

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        right: 0,
        zIndex: 99999,
        background: "rgba(0,0,0,0.88)",
        color: "#fff",
        font: "12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace",
        padding: "10px 14px",
        borderTopLeftRadius: 8,
        pointerEvents: "none",
        maxWidth: "90vw",
      }}
    >
      {rows.map(([label, value]) => (
        <div key={label}>
          <span style={{ opacity: 0.65 }}>{label}: </span>
          <strong>{String(value)}</strong>
        </div>
      ))}
    </div>
  );
}
